class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.scrollLockState = null;
    this.noteActiveElement = null;
    this.addEventListener('keyup', (event) => {
      if (event.code !== 'Escape') return;
      if (this.querySelector('#HugfigCartNoteDialog:not([hidden])')) {
        this.closeNoteDialog();
        return;
      }
      this.close();
    });
    this.addEventListener('click', this.handleDrawerClick.bind(this));
    this.addEventListener('input', this.handleDrawerInput.bind(this));
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.refreshScrollCue();
  }

  handleDrawerClick(event) {
    const noteOpen = event.target.closest('[data-cart-note-open]');
    if (noteOpen) {
      event.preventDefault();
      this.openNoteDialog(noteOpen);
      return;
    }

    if (event.target.closest('[data-cart-note-close]')) {
      event.preventDefault();
      this.closeNoteDialog();
      return;
    }

    const noteSave = event.target.closest('[data-cart-note-save]');
    if (noteSave) {
      event.preventDefault();
      this.saveNote(noteSave);
    }
  }

  handleDrawerInput(event) {
    if (!event.target.matches('#HugfigCartNote')) return;
    const counter = this.querySelector('[data-cart-note-count]');
    if (counter) counter.textContent = event.target.value.length;
  }

  openNoteDialog(trigger) {
    const dialog = this.querySelector('#HugfigCartNoteDialog');
    if (!dialog) return;

    this.noteActiveElement = trigger;
    dialog.hidden = false;
    const textarea = dialog.querySelector('#HugfigCartNote');
    requestAnimationFrame(() => {
      textarea?.focus({ preventScroll: true });
      if (typeof trapFocus === 'function') trapFocus(dialog, textarea || dialog);
    });
  }

  closeNoteDialog() {
    const dialog = this.querySelector('#HugfigCartNoteDialog');
    if (!dialog || dialog.hidden) return;

    dialog.hidden = true;
    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.noteActiveElement);
    this.noteActiveElement?.focus({ preventScroll: true });
    this.noteActiveElement = null;
  }

  async saveNote(button) {
    const textarea = this.querySelector('#HugfigCartNote');
    const status = this.querySelector('[data-cart-note-status]');
    if (!textarea || button.disabled) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    if (status) status.textContent = 'Saving…';

    try {
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({ note: textarea.value.trim() }),
      });
      if (!response.ok) throw new Error('Unable to save the order note.');

      const cart = await response.json();
      const note = cart.note || '';
      const trigger = this.querySelector('.hugfig-cart-note-trigger');
      const previewButton = this.querySelector('.hugfig-cart-note-preview');
      const preview = this.querySelector('[data-cart-note-preview]');
      const title = this.querySelector('#HugfigCartNoteTitle');

      if (trigger) trigger.textContent = note ? 'Edit order note' : 'Add order note';
      if (previewButton) {
        previewButton.classList.toggle('is-hidden', !note);
        previewButton.title = note;
      }
      if (preview) preview.textContent = note;
      if (title) title.textContent = note ? 'Edit order note' : 'Add order note';
      if (status) status.textContent = '';
      this.closeNoteDialog();
    } catch (error) {
      if (status) status.textContent = error.message || 'Unable to save. Please try again.';
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    this.lockPageScroll();
    this.refreshScrollCue();

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.closeNoteDialog();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    this.unlockPageScroll();
  }

  lockPageScroll() {
    if (this.scrollLockState) return;

    const body = document.body;
    this.scrollLockState = {
      scrollY: window.scrollY,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = 'fixed';
    body.style.top = `-${this.scrollLockState.scrollY}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.classList.add('overflow-hidden');
  }

  unlockPageScroll() {
    if (!this.scrollLockState) return;

    const body = document.body;
    const { scrollY, position, top, width, overflow } = this.scrollLockState;
    body.style.position = position;
    body.style.top = top;
    body.style.width = width;
    body.style.overflow = overflow;
    body.classList.remove('overflow-hidden');
    this.scrollLockState = null;
    window.scrollTo(0, scrollY);
  }

  refreshScrollCue() {
    // Rebind after Shopify section rendering replaces the drawer contents.
    this.scrollCueAbortController?.abort();
    this.scrollCueResizeObserver?.disconnect();

    const region = this.querySelector('[data-cart-items-scroll-region]');
    const cue = this.querySelector('[data-cart-scrollbar]');
    const thumb = this.querySelector('[data-cart-scrollbar-thumb]');
    if (!region || !cue || !thumb) return;

    const abortController = new AbortController();
    this.scrollCueAbortController = abortController;

    const updateScrollCue = () => {
      const hasOverflow = region.scrollHeight > region.clientHeight + 1;
      cue.hidden = !hasOverflow;
      if (!hasOverflow) return;

      const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
      const touchTarget = Number.parseFloat(getComputedStyle(this).getPropertyValue('--hf-rb-touch-target')) || 4.4;
      const minimumThumbSize = touchTarget * rootFontSize;
      const thumbSize = Math.min(
        cue.clientHeight,
        Math.max(minimumThumbSize, cue.clientHeight * (region.clientHeight / region.scrollHeight))
      );
      const availableTravel = Math.max(0, cue.clientHeight - thumbSize);
      const scrollProgress = region.scrollTop / Math.max(1, region.scrollHeight - region.clientHeight);

      thumb.style.setProperty('height', `${thumbSize}px`, 'important');
      thumb.style.setProperty('transform', `translateY(${availableTravel * scrollProgress}px)`, 'important');
    };

    region.addEventListener('scroll', updateScrollCue, { passive: true, signal: abortController.signal });
    if ('ResizeObserver' in window) {
      this.scrollCueResizeObserver = new ResizeObserver(updateScrollCue);
      this.scrollCueResizeObserver.observe(region);
      const itemList = region.querySelector('.cart-items');
      if (itemList) this.scrollCueResizeObserver.observe(itemList);
    }

    requestAnimationFrame(updateScrollCue);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.refreshScrollCue();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
